import { recruiterEnv } from '@/config/env';
import { getKeycloak } from '@/auth/keycloak-instance';
import { triggerDownload } from '@/shared/xlsx';

/**
 * EU-593 — "Exportar inscritos" (Opção B: proxy no BFF).
 *
 * O `.xlsx` da base CRUA de inscritos é servido pela API do rank e passa pelo
 * BFF (`GET /programs/:programId/subscribers-export`), que encaminha o bearer
 * do consultor e faz o stream de volta — a credencial NUNCA chega ao browser.
 * Aqui o SPA faz um `fetch` cru (o download é BINÁRIO — o `fetchClient` do SDK
 * lê JSON/texto e corromperia os bytes), recebe o blob e dispara o download
 * nativo (`URL.createObjectURL` + `a.click()`), sem nova aba nem `window.open`.
 */

/** Erro do download com o status HTTP do BFF, para o mapeamento de toast. */
export class SubscribersExportError extends Error {
  readonly name = 'SubscribersExportError';
  constructor(readonly status: number) {
    super(`Subscribers export failed (status ${status})`);
  }
}

/** FR-003 — nome quando o BFF/rank não devolve Content-Disposition. */
const FALLBACK_FILE_NAME = 'Base de Inscritos.xlsx';

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // `attachment; filename="Base - X.xlsx"` (ou RFC 5987 `filename*=UTF-8''…`).
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1].trim());
  } catch {
    return match[1].trim();
  }
}

/**
 * Baixa a base de inscritos do programa via BFF. Lança `SubscribersExportError`
 * (com o status) quando o BFF não responde 2xx — o container mapeia para toast.
 */
export async function downloadSubscribersBase(programId: string): Promise<void> {
  const keycloak = getKeycloak();
  try {
    // Refresca o token antes do fetch cru (o SDK faz isso por request; aqui
    // não passamos pelo SDK). Falha → segue com o token atual e o BFF/rank
    // respondem 403 → toast claro.
    await keycloak.updateToken(30);
  } catch {
    // token expirado → o BFF devolve 403; o container avisa e pede re-login.
  }
  const token = keycloak.token;

  const url = `${recruiterEnv.apiUrl}/programs/${programId}/subscribers-export`;
  const res = await fetch(url, {
    method: 'GET',
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    throw new SubscribersExportError(res.status);
  }

  const blob = await res.blob();
  const fileName =
    filenameFromContentDisposition(res.headers.get('content-disposition')) ?? FALLBACK_FILE_NAME;
  triggerDownload(blob, fileName);
}

/**
 * Mapeia o status do BFF (espelho do rank) para a mensagem de toast do
 * consultor. Rede/timeout/desconhecido caem no genérico re-tentável.
 */
export function subscribersExportErrorMessage(status: number): string {
  switch (status) {
    case 403:
      return 'Você não tem acesso ao rank.';
    case 409:
      return 'Sessão sem carteira — saia e entre novamente.';
    case 503:
      return 'Base indisponível, tente em instantes.';
    case 400:
      return 'Programa inválido.';
    default:
      return 'Não foi possível gerar a base, tente novamente.';
  }
}
